import * as React from "react";
import { Info } from "lucide-react";
import { cn } from "./cn.js";

export function Card({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "rounded-xl border border-slate-200 bg-white shadow-panel dark:border-slate-800 dark:bg-slate-900",
        className,
      )}
      {...props}
    />
  );
}

export function CardHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex items-center justify-between gap-2 border-b border-slate-100 px-5 py-4 dark:border-slate-800", className)} {...props} />;
}

export function CardTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return <h3 className={cn("text-sm font-semibold text-ink dark:text-slate-100", className)} {...props} />;
}

export function CardContent({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("px-5 py-4", className)} {...props} />;
}

export function KpiCard({
  label,
  value,
  hint,
  info,
  onClick,
}: {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  /** Kurzer Erklaerungstext, der ueber ein kleines Info-Symbol per Hover/Fokus eingeblendet wird -
   * fuer Kennzahlen, deren genaue Bedeutung/Berechnung nicht aus Label + Wert allein ersichtlich ist. */
  info?: string;
  onClick?: () => void;
}) {
  const Comp = onClick ? "button" : "div";
  return (
    <Comp
      onClick={onClick}
      className={cn(
        "flex flex-col gap-1 rounded-xl border border-slate-200 bg-white p-4 text-left shadow-panel dark:border-slate-800 dark:bg-slate-900",
        onClick && "cursor-pointer transition-shadow hover:shadow-md",
      )}
    >
      <span className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
        {info && (
          <span className="group relative inline-flex shrink-0">
            <Info
              className="h-3.5 w-3.5 cursor-help text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
              tabIndex={0}
              role="img"
              aria-label={info}
            />
            <span
              role="tooltip"
              className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 w-56 -translate-x-1/2 rounded-md bg-ink px-2.5 py-1.5 text-xs normal-case tracking-normal text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 dark:bg-slate-700"
            >
              {info}
            </span>
          </span>
        )}
      </span>
      <span className="text-2xl font-semibold text-ink dark:text-slate-50">{value}</span>
      {hint && <span className="text-xs text-slate-500 dark:text-slate-400">{hint}</span>}
    </Comp>
  );
}
